import React, { useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button,
  Input,
  useDisclosure,
} from "@nextui-org/react";
import { IoBarChart, IoSettingsSharp } from "react-icons/io5";
import {
  FaLayerGroup,
  FaUniversity,
  FaMapMarkerAlt,
  FaUserShield,
  FaHandHoldingUsd,
  FaMoneyCheckAlt,
  FaUserCog,
  FaChartLine,
  FaBuilding,
} from "react-icons/fa";
import { IoSearchSharp } from "react-icons/io5";
import { Link, useLocation } from "react-router-dom";
import { MdAssessment, MdTitle } from "react-icons/md";
import { checkPermissions } from "../../utils/getRolesAndPermissions.js";

export const hasAnySettingsPermission = () => {
  const settingsPermissions = [
    "BANK_CATEGORY_VIEW",
    "BANK_VIEW",
    "BANK_BRANCH_VIEW",
    "BANK_QUARTERLY_REPORT_VIEW",
    "SHARE_SCRIPT_MANAGEMENT_VIEW",
    "BROKER_MANAGEMENT_VIEW",
    "DEBENTURE_SCRIPT_MANAGEMENT_VIEW",
    "MUTUAL_FUND_SCRIPT_MANAGEMENT_VIEW",
    "INVESTMENT_DIRECTIVE_TITLES_VIEW",
    "INVESTMENT_DIRECTIVE_VALUES_VIEW",
    "ALT_INVESTMENT_COMPANY_VIEW",
    "USER_PERMISSION_MANAGEMENT_VIEW",
    "USER_ROLES_MANAGEMENT_VIEW",
    "USER_MANAGEMENT_VIEW",
    "SHARE",
    "DEBENTURE",
    "MUTUALFUND",
    "PREFERENCE_SHARE_VIEW",
    "GOVERNMENT_BOND_VIEW",
    "GOVERNMENT_BOND_SCRIPT_MANAGEMENT_VIEW",
  ];

  return settingsPermissions.some((permission) =>
    checkPermissions(permission, true),
  );
};

export default function Setting() {
  const location = useLocation().pathname;
  const [searchTerm, setSearchTerm] = useState("");
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();

  const isActive = (path) => location.includes(path);

  // Grouped settings
  const settingGroups = [
    // Bank Settings Group
    (() => {
      const bankItems = [
        checkPermissions("BANK_CATEGORY_VIEW", true) && {
          label: "Bank Categories",
          icon: <FaLayerGroup className="text-lg text-black/70 scale-90" />,
          link: "/setting/bank-category",
        },
        checkPermissions("BANK_VIEW", true) && {
          label: "Banks",
          icon: <FaUniversity className="text-lg text-black/70" />,
          link: "/setting/banks",
        },
        checkPermissions("BANK_BRANCH_VIEW", true) && {
          label: "Bank Branches",
          icon: <FaMapMarkerAlt className="text-lg text-black/70" />,
          link: "/setting/bank-branch",
        },
        checkPermissions("BANK_QUARTERLY_REPORT_VIEW", true) && {
          label: "Bank Quarterly Reports",
          icon: <FaChartLine className="text-lg text-black/70" />,
          link: "/setting/bank-quarterly-reports",
        },
      ].filter(Boolean);

      return bankItems.length > 0
        ? {
            title: "Bank Settings",
            items: bankItems,
          }
        : null;
    })(),

    // Manual Entry Settings Group
    (() => {
      const manualItems = [
        checkPermissions("SHARE", true) && {
          label: "Create Manual Share",
          icon: <MdAssessment className="text-lg text-black/70" />,
          link: "/setting/manual-share",
        },
        checkPermissions("DEBENTURE", true) && {
          label: "Create Manual Debenture",
          icon: <FaHandHoldingUsd className="text-lg text-black/70" />,
          link: "/setting/manual-debenture",
        },
        checkPermissions("MUTUALFUND", true) && {
          label: "Create Manual Mutual Fund",
          icon: <IoBarChart className="text-lg fill-current text-black/70" />,
          link: "/setting/manual-mutualfund",
        },
        checkPermissions("PREFERENCE_SHARE_VIEW", true) && {
          label: "Create Manual Preference Share",
          icon: <IoBarChart className="text-lg fill-current text-black/70" />,
          link: "/setting/manual-preferenceshare",
        },
        checkPermissions("GOVERNMENT_BOND_VIEW", true) && {
          label: "Create Manual Government Bond",
          icon: <FaHandHoldingUsd className="text-lg text-black/70" />,
          link: "/setting/manual-government-bond",
        },
      ].filter(Boolean);

      return manualItems.length > 0
        ? {
            title: "Manual Entry Settings",
            items: manualItems,
          }
        : null;
    })(),

    // Investment Settings Group
    (() => {
      const investmentItems = [
        checkPermissions("SHARE_SCRIPT_MANAGEMENT_VIEW", true) && {
          label: "Manage Share Script",
          icon: <MdAssessment className="text-lg text-black/70" />,
          link: "/setting/sharescript",
        },
        checkPermissions("BROKER_MANAGEMENT_VIEW", true) && {
          label: "Manage Broker List",
          icon: <FaBuilding className="text-lg text-black/70" />,
          link: "/setting/brokerlist",
        },
        checkPermissions("DEBENTURE_SCRIPT_MANAGEMENT_VIEW", true) && {
          label: "Manage Debenture Script",
          icon: <FaHandHoldingUsd className="text-lg text-black/70" />,
          link: "/setting/debenturescript",
        },
        checkPermissions("MUTUAL_FUND_SCRIPT_MANAGEMENT_VIEW", true) && {
          label: "Manage Mutual Fund Script",
          icon: <IoBarChart className="text-lg fill-current text-black/70" />,
          link: "/setting/mutualfundscript",
        },
        checkPermissions("GOVERNMENT_BOND_SCRIPT_MANAGEMENT_VIEW", true) && {
          label: "Manage Government Bond Script",
          icon: <FaHandHoldingUsd className="text-lg text-black/70" />,
          link: "/setting/governmentbondscript",
        },
        checkPermissions("INVESTMENT_DIRECTIVE_TITLES_VIEW", true) && {
          label: "Investment Directive Titles",
          icon: <MdTitle className="text-lg fill-current text-black/70" />,
          link: "/setting/investment-directive-titles",
        },
        checkPermissions("INVESTMENT_DIRECTIVE_VALUES_VIEW", true) && {
          label: "Investment Directive Values",
          icon: <FaMoneyCheckAlt className="text-lg text-black/70" />,
          link: "/setting/investment-directive-value",
        },
        checkPermissions("ALT_INVESTMENT_COMPANY_VIEW", true) && {
          label: "Alternative Investment Companies",
          icon: <FaBuilding className="text-lg text-black/70" />,
          link: "/setting/alternative-investment-company",
        },
      ].filter(Boolean);

      return investmentItems.length > 0
        ? {
            title: "Investment Settings",
            items: investmentItems,
          }
        : null;
    })(),

    // User Management Group
    (() => {
      const userItems = [
        checkPermissions("USER_PERMISSION_MANAGEMENT_VIEW", true) && {
          label: "Permission Management",
          icon: <FaUserCog className="text-lg text-black/70" />,
          link: "/setting/permission-management",
        },
        checkPermissions("USER_ROLES_MANAGEMENT_VIEW", true) && {
          label: "Roles Management",
          icon: <FaUserShield className="text-lg text-black/70" />,
          link: "/setting/roles-management",
        },
        checkPermissions("USER_MANAGEMENT_VIEW", true) && {
          label: "Manage Users",
          icon: <FaUserShield className="text-lg text-black/70" />,
          link: "/setting/users",
        },
      ].filter(Boolean);

      return userItems.length > 0
        ? {
            title: "User Management",
            items: userItems,
          }
        : null;
    })(),
  ].filter((group) => group !== null); // Remove any null groups

  // Filter items based on search term
  const filteredGroups = settingGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) =>
        item.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    }))
    .filter((group) => group.items.length > 0);

  const handleItemClick = () => {
    setSearchTerm("");
    onClose(); // Close the popover when an item is clicked
  };

  return (
    <Popover
      radius="none"
      placement="bottom-end"
      className="border-none !rounded-lg !shadow-lg"
      shadow="sm"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <PopoverTrigger>
        <Button
          isIconOnly
          size="sm"
          className={`bg-transparent hover:bg-gray-200 ${
            location.includes("/setting")
              ? "bg-[#2344d620] border border-[#2344d620]"
              : ""
          }`}
          radius="full"
          aria-label="Settings"
          onPress={onOpen}
        >
          <IoSettingsSharp className="text-2xl scale-85 text-black/70" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-[240px] !shadow-sm border !rounded bg-[#ffffff] p-2 flex flex-col justify-start items-start gap-1">
        <div className="w-full px-1 py-2">
          <Input
            size="sm"
            variant="bordered"
            endContent={<IoSearchSharp className="text-lg text-black/70" />}
            placeholder="Search settings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            classNames={{
              inputWrapper: "h-8",
              input: "text-sm",
            }}
          />
        </div>

        <div className="max-h-[260px] px-1 overflow-auto w-full">
          {filteredGroups.map((group, groupIndex) => (
            <div key={groupIndex} className="mb-2">
              <div className="font-roboto text-xs font-semibold text-gray-500 px-2 py-1 uppercase tracking-wider">
                {group.title}
              </div>
              {group.items.map((item, itemIndex) => (
                <Link
                  key={`${groupIndex}-${itemIndex}`}
                  to={item.link}
                  className={`flex w-full items-center pl-2 pr-2.5 gap-3 ${
                    isActive(item.link) ? "bg-[#2344d650]" : ""
                  } hover:bg-[#2344d630] p-1.5 rounded cursor-pointer`}
                  onClick={handleItemClick}
                >
                  {item.icon}
                  <span className="text-black/80 text-sm font-roboto">
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
          ))}

          {filteredGroups.length === 0 && (
            <div className="text-center text-sm text-gray-500 p-2">
              No settings found
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
